import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import {
  ArrowRight,
  Building2,
  Loader2,
  MapPin,
  Search,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { apiFetch, ApiError } from '../../../lib/api';
import { useComparateurStore } from './useComparateurStore';
import { StepEyebrow } from './StepEyebrow';
import {
  type InseeSearchResult,
  siretSchema,
} from '@shared/schemas/comparateur';
import { cn } from '../../../lib/cn';

function formatSiret(digits: string): string {
  const parts = [
    digits.slice(0, 3),
    digits.slice(3, 6),
    digits.slice(6, 9),
    digits.slice(9, 14),
  ];
  return parts.filter(Boolean).join(' ');
}

function formatSiren(digits: string): string {
  const parts = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9)];
  return parts.filter(Boolean).join(' ');
}

const SEARCH_DEBOUNCE_MS = 250;

export function StepCompanyLookup() {
  const store = useComparateurStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<InseeSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);

  const digitsOnly = query.replace(/\D/g, '');
  const isSiret = digitsOnly.length === 14 && /^\d{14}$/.test(digitsOnly);
  const isSiren = digitsOnly.length === 9 && /^\d{9}$/.test(digitsOnly);
  const queryTrimmed = query.trim();

  const fetchResults = useCallback(async (q: string) => {
    setSearching(true);
    setSearchError(null);
    try {
      const { hits } = await apiFetch<{ hits: InseeSearchResult[] }>(
        '/api/comparateur/insee/search',
        { json: { query: q, limit: 8 } },
      );
      setResults(hits);
      setHighlight(0);
    } catch (err) {
      setSearchError(err instanceof ApiError ? err.message : 'Recherche indisponible');
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (queryTrimmed.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      void fetchResults(queryTrimmed);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [queryTrimmed, fetchResults]);

  useEffect(() => {
    const t = window.setTimeout(() => inputRef.current?.focus(), 80);
    return () => window.clearTimeout(t);
  }, []);

  function onSelectHit(hit: InseeSearchResult): void {
    void store.selectCompany(hit.siret);
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (isSiret) {
      const parsed = siretSchema.safeParse(digitsOnly);
      if (parsed.success) {
        void store.selectCompany(parsed.data);
      }
      return;
    }
    if (results.length > 0 && results[highlight]) {
      onSelectHit(results[highlight]);
    }
  }

  function onInputChange(e: ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value;
    setQuery(value.slice(0, 120));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter' && !isSiret) {
      e.preventDefault();
      const hit = results[highlight];
      if (hit) onSelectHit(hit);
    }
  }

  const showResults =
    !isSiret && (results.length > 0 || searching || (queryTrimmed.length >= 2 && !searchError));

  return (
    <div ref={containerRef}>
      <StepEyebrow step="lookup" />
      <h3 className="mt-2 h-section">
        Quelle est votre entreprise ?
      </h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
        Saisissez votre raison sociale ou votre SIRET. Nous adaptons automatiquement la comparaison à votre profil et votre secteur d'activité.
      </p>

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <div className="relative">
          <Label htmlFor="company-query" className="text-[13px] font-semibold">
            Raison sociale ou SIRET
          </Label>

          <div className="relative mt-1.5">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <input
              ref={inputRef}
              id="company-query"
              type="text"
              autoComplete="organization"
              role="combobox"
              aria-expanded={showResults}
              aria-controls="company-search-results"
              aria-activedescendant={
                results[highlight] ? `company-hit-${results[highlight].siret}` : undefined
              }
              required
              value={query}
              onChange={onInputChange}
              onKeyDown={onKeyDown}
              placeholder="Solution Éco France ou 982 431 033 00012"
              className={cn(
                'h-12 w-full rounded-xl border-2 bg-white pl-10 pr-12 text-[15px] tracking-tight text-ink placeholder:text-stone-300 transition-colors',
                'border-stone-200 focus:border-brand-400 focus:outline-none',
              )}
            />
            {(isSiret || isSiren) && (
              <span className="absolute right-3.5 top-1/2 -translate-y-1/2 rounded-md bg-brand-50 px-1.5 py-0.5 font-mono text-[10px] font-bold text-brand-700">
                {isSiret ? 'SIRET' : 'SIREN'}
              </span>
            )}
            {searching && !isSiret && (
              <Loader2 className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-stone-400" />
            )}
          </div>

          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-stone-400">
            <ShieldCheck className="h-3 w-3 shrink-0 text-brand-500" />
            Auto-complétion via le registre officiel des entreprises — aucune donnée stockée hors UE.
          </div>

          {showResults && (
            <div
              id="company-search-results"
              role="listbox"
              className="mt-2 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-[0_4px_24px_rgba(0,0,0,.07)]"
            >
              {searching && results.length === 0 ? (
                <div className="flex items-center gap-2 px-4 py-3 text-[12px] text-stone-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Recherche en cours…
                </div>
              ) : results.length === 0 ? (
                <p className="px-4 py-3 text-[12px] text-stone-500">
                  Aucun résultat. Essayez un autre mot-clé ou saisissez directement votre
                  numéro SIRET (14 chiffres).
                </p>
              ) : (
                <ul className="max-h-72 overflow-y-auto">
                  {results.map((hit, i) => {
                    const active = i === highlight;
                    return (
                      <li key={hit.siret}>
                        <button
                          id={`company-hit-${hit.siret}`}
                          type="button"
                          role="option"
                          aria-selected={active}
                          onMouseEnter={() => setHighlight(i)}
                          onClick={() => onSelectHit(hit)}
                          disabled={store.loading}
                          className={cn(
                            'flex w-full items-start gap-3 border-b border-stone-100 px-3.5 py-3 text-left transition-colors last:border-b-0',
                            active ? 'bg-brand-50/60' : 'bg-white hover:bg-stone-50',
                          )}
                        >
                          <span
                            className={cn(
                              'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
                              active ? 'bg-brand-100' : 'bg-stone-100',
                            )}
                          >
                            <Building2
                              className={cn(
                                'h-3.5 w-3.5',
                                active ? 'text-brand-700' : 'text-stone-500',
                              )}
                              strokeWidth={1.8}
                            />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13.5px] font-semibold text-ink">
                              {hit.name}
                            </span>
                            <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-stone-500">
                              <span className="font-mono tabular-nums">
                                SIRET {formatSiret(hit.siret)}
                              </span>
                              {hit.nafCode && (
                                <span>
                                  NAF{' '}
                                  <strong className="text-stone-700">{hit.nafCode}</strong>
                                </span>
                              )}
                              {(hit.postalCode || hit.city) && (
                                <span className="inline-flex items-center gap-1">
                                  <MapPin className="h-2.5 w-2.5" />
                                  {[hit.postalCode, hit.city].filter(Boolean).join(' ')}
                                </span>
                              )}
                            </span>
                          </span>
                          <ArrowRight
                            className={cn(
                              'mt-1 h-3.5 w-3.5 shrink-0 transition-transform',
                              active
                                ? 'translate-x-0.5 text-brand-600'
                                : 'text-stone-300',
                            )}
                          />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {searchError && (
            <Alert variant="warning" className="mt-3">
              <AlertDescription className="text-[12px]">{searchError}</AlertDescription>
            </Alert>
          )}
        </div>

        {store.error && (
          <Alert variant="error">
            <AlertDescription className="text-[12px]">{store.error}</AlertDescription>
          </Alert>
        )}

        {isSiret && (
          <div className="rounded-xl border border-brand-200 bg-brand-50/40 p-3 text-[12px] text-brand-900">
            <p className="font-semibold">SIRET détecté : {formatSiret(digitsOnly)}</p>
            <p className="mt-1 text-brand-800">
              Cliquez sur «&nbsp;Identifier mon entreprise&nbsp;» pour interroger le registre officiel.
            </p>
          </div>
        )}
        {isSiren && (
          <p className="rounded-xl border border-stone-200 bg-stone-50 p-3 text-[12px] text-stone-700">
            SIREN détecté ({formatSiren(digitsOnly)}). Choisissez l'établissement dans la liste
            ci-dessus ou ajoutez les 5 derniers chiffres pour obtenir le SIRET complet.
          </p>
        )}

        <Button
          type="submit"
          variant="brand"
          size="xl"
          disabled={
            store.loading || (!isSiret && results.length === 0) || queryTrimmed.length < 2
          }
          className="w-full gap-2"
        >
          {store.loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Récupération en cours…
            </>
          ) : (
            <>
              {isSiret ? 'Identifier mon entreprise' : 'Continuer avec ce résultat'}
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>

        <p className="flex items-center justify-center gap-1.5 text-[11px] text-stone-400">
          <Sparkles className="h-3 w-3" />
          Comparateur réservé aux professionnels et entreprises, pas aux particuliers.
        </p>
      </form>

      {/* Enrichissement gouvernemental après sélection */}
      {store.dossier?.company?.siret && (
        <EntrepriseGouvCard siret={store.dossier.company.siret} />
      )}
    </div>
  );
}

/* ── Carte enrichissement entreprise.gouv.fr ─────────────── */

interface EntrepriseGouvDetail {
  nafLabel: string | null;
  certifications: string[];
  conventionCollective: string | null;
  formeJuridique: string | null;
  isRge: boolean;
  rgeDomainesTravaux: string[];
}

function EntrepriseGouvCard({ siret }: { siret: string }) {
  const [detail, setDetail] = useState<EntrepriseGouvDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/entreprises/siret/${siret}`)
      .then((r) => r.ok ? r.json() : null)
      .then((json: { result?: EntrepriseGouvDetail } | null) => {
        if (!cancelled && json?.result) setDetail(json.result);
      })
      .catch(() => {/* silent */})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [siret]);

  if (loading) return null;
  if (!detail) return null;

  const hasExtra = detail.certifications.length > 0 || detail.conventionCollective || detail.nafLabel;
  if (!hasExtra) return null;

  return (
    <div className="mt-4 rounded-xl border border-stone-200 bg-stone-50/60 p-3.5">
      <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-stone-400">
        <ShieldCheck className="h-3 w-3 text-brand-500" strokeWidth={2} />
        Données registre officiel (recherche-entreprises.api.gouv.fr)
      </p>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11.5px] text-stone-600">
        {detail.nafLabel && (
          <span><strong className="text-stone-800">Activité :</strong> {detail.nafLabel}</span>
        )}
        {detail.formeJuridique && (
          <span><strong className="text-stone-800">Forme :</strong> {detail.formeJuridique}</span>
        )}
        {detail.conventionCollective && (
          <span><strong className="text-stone-800">Conv. collective :</strong> {detail.conventionCollective}</span>
        )}
      </div>
      {detail.certifications.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {detail.certifications.map((cert) => (
            <span
              key={cert}
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                cert === 'RGE'
                  ? 'bg-brand-100 text-brand-800 ring-1 ring-inset ring-brand-200'
                  : 'bg-stone-100 text-stone-700 ring-1 ring-inset ring-stone-200',
              )}
            >
              {cert === 'RGE' && <ShieldCheck className="h-2.5 w-2.5" strokeWidth={2.5} />}
              {cert}
            </span>
          ))}
        </div>
      )}
      {detail.isRge && detail.rgeDomainesTravaux.length > 0 && (
        <p className="mt-1.5 text-[10.5px] text-stone-400">
          Domaines RGE : {detail.rgeDomainesTravaux.join(' · ')}
        </p>
      )}
    </div>
  );
}
